import { getStopsOptions } from 'constants/flight';
import { FlightSearch, Outbound } from 'ts/flight/interfaces/FlightSearch';
import { TimeFilter } from 'ts/flight/classes/FlightFilterModel';
import { getBounds } from './flight';
import { formatPriceCurrency } from 'utils/utils';
import { checkNoBaggage } from './flight-filter';

export const getStopsInbound = (flights: FlightSearch[], currencySign: string) => {
  return getStopsOptions(false).map((stop) => {
    const prices = flights
      .filter((flight) => {
        const outbondStopsCount = flight.outbound?.legCount - 1;
        const inboundStopsCount = flight.inbound?.legCount - 1;
        if (stop.number === 0.5) {
          return outbondStopsCount === 0 && inboundStopsCount > 0;
        } else if (stop.number === 2) {
          return outbondStopsCount >= stop.number && inboundStopsCount >= stop.number;
        } else {
          return stop.number === outbondStopsCount && stop.number === inboundStopsCount;
        }
      })
      .map((flight) => flight.flighPrice?.grossAndTaxWithMarkup.value);

    const minPrice = prices.length ? Math.min(...prices) : 0;

    return {
      ...stop,
      minPrice: formatPriceCurrency(minPrice, currencySign),
      checked: false,
      disabled: !prices.length,
    };
  });
};

export const getStops = (flights: FlightSearch[], currencySign: string) => {
  return getStopsOptions(true).map((stop) => {
    const prices = flights
      .filter((flight) => {
        const bounds = getBounds(flight);

        if (stop.number === 2) {
          return bounds.every((bound) => bound.legCount - 1 >= stop.number);
        }

        return bounds.every((bound) => bound.legCount - 1 === stop.number);
      })
      .map((flight) => flight.flighPrice?.grossAndTaxWithMarkup.value);

    const minPrice = prices.length ? Math.min(...prices) : 0;
    return {
      ...stop,
      minPrice: formatPriceCurrency(minPrice, currencySign),
      checked: false,
      disabled: !prices.length,
    };
  });
};

export const getBaggage = (flights: FlightSearch[], currencySign: string) => {
  const checkBaggage = (bounds: Outbound[], baggage: any) => {
    if (baggage.number === 0) {
      return bounds.every((bound) => checkNoBaggage(bound));
    } else if (baggage.number === 1) {
      return bounds.some((bound) => !checkNoBaggage(bound));
    }
    return false;
  };
  return [
    { number: 0, label: 'filter.baggage_not_included' },
    { number: 1, label: 'filter.baggage_included' },
  ].map((baggage) => {
    const prices = flights
      .filter((flight) => {
        const bounds = getBounds(flight);
        return checkBaggage(bounds, baggage);
      })
      .map((flight) => flight.flighPrice?.grossAndTaxWithMarkup.value);

    const minPrice = prices.length ? Math.min(...prices) : 0;
    return {
      ...baggage,
      minPrice: formatPriceCurrency(minPrice, currencySign),
      checked: false,
      disabled: !prices.length,
    };
  });
};

export const getAirlines = (flights: FlightSearch[], currencySign: string) => {
  return flights
    .map((flight) => flight.airlineCode)
    .filter((airline, index, arr) => arr.indexOf(airline) === index)
    .map((airline) => {
      const minPrice = Math.min(
        ...flights
          .filter((flight) => flight.airlineCode === airline)
          .map((flight) => flight.flighPrice?.grossAndTaxWithMarkup.value)
      );

      const label = flights.find((flight) => flight.airlineCode === airline)?.airlineDesc || '';

      return {
        code: airline || '',
        label,
        checked: false,
        minPrice: formatPriceCurrency(minPrice, currencySign),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getAirports = (flights: FlightSearch[], currencySign: string) => {
  return flights
    .reduce((acc: string[], flight) => {
      const bounds = getBounds(flight);
      const airports = bounds
        .map((bound) => [bound.departureAirport.code, bound.arrivalAirport.code])
        .flat();
      return [...acc, ...airports];
    }, [])
    .filter((airport, index, arr) => arr.indexOf(airport) === index)
    .map((airport) => {
      const getAirtport = (flight: FlightSearch) => {
        const bounds = getBounds(flight);
        return (
          bounds.find((bound) => bound.departureAirport.code === airport) ||
          bounds.find((bound) => bound.arrivalAirport.code === airport)
        );
      };

      const minPrice = Math.min(
        ...flights
          .filter(getAirtport)
          .map((flight) => flight.flighPrice?.grossAndTaxWithMarkup.value)
      );

      let label = airport;
      const labelFlight = flights.find(getAirtport);
      if (labelFlight) {
        const labelBounds = getBounds(labelFlight);
        const labelBound = labelBounds.find(
          (bound) =>
            bound.departureAirport.code === airport || bound.arrivalAirport.code === airport
        );
        const labelAirport =
          labelBound?.departureAirport.code === airport
            ? labelBound?.departureAirport
            : labelBound?.arrivalAirport;
        label = labelAirport?.desc + ', ' + labelAirport?.cityDesc;
      }

      return {
        code: airport || '',
        label,
        checked: false,
        minPrice: formatPriceCurrency(minPrice, currencySign),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getFlightTypes = (flights: FlightSearch[], currencySign: string) => {
  return flights
    .map((flight) => flight.flightSource)
    .filter((type, index, arr) => arr.indexOf(type) === index && type)
    .map((type) => {
      const minPrice = Math.min(
        ...flights
          .filter((flight) => flight.flightSource === type)
          .map((flight) => flight.flighPrice?.grossAndTaxWithMarkup.value)
      );

      return {
        code: type || '',
        label: type,
        minPrice: formatPriceCurrency(minPrice, currencySign),
        checked: false,
      };
    });
};

export const getTimes = (times: number[]): TimeFilter => {
  const minArrivalTime = Math.floor(times.length ? Math.min(...times) : 0);
  const maxArrivalTime = Math.ceil(times.length ? Math.max(...times) : 1);

  return {
    min: minArrivalTime,
    max: maxArrivalTime,
    value: [minArrivalTime, maxArrivalTime],
  };
};
